import { useCallback, useEffect, useRef, useState } from "react";

type MediaStreamState = {
  stream: MediaStream | null;
  error: "denied" | "unavailable" | null;
  videoActive: boolean;
  audioActive: boolean;
  isLoading: boolean;
  start: () => Promise<void>;
  stop: () => void;
};

export function useMediaStream(): MediaStreamState {
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [error, setError] = useState<"denied" | "unavailable" | null>(null);
  const [videoActive, setVideoActive] = useState(false);
  const [audioActive, setAudioActive] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const streamRef = useRef<MediaStream | null>(null);

  const stop = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    setStream(null);
    setVideoActive(false);
    setAudioActive(false);
  }, []);

  const start = useCallback(async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setError("unavailable");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      stop();
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true,
      });
      streamRef.current = mediaStream;
      setStream(mediaStream);
      setVideoActive(
        mediaStream.getVideoTracks().some((track) => track.readyState === "live")
      );
      setAudioActive(
        mediaStream.getAudioTracks().some((track) => track.readyState === "live")
      );
    } catch {
      setError("denied");
      stop();
    } finally {
      setIsLoading(false);
    }
  }, [stop]);

  useEffect(() => () => stop(), [stop]);

  return { stream, error, videoActive, audioActive, isLoading, start, stop };
}
